/**
 * @file useLegalFileUpload.ts
 * @description Загрузка PDF документа с процентом и отменой (phase19.16,
 * SB139; uiux-legal-docs §7: «Загружаем cert.pdf — 64% [Отменить]»).
 *
 * До отправки — проверка в браузере тем же пределом и типом, что у API
 * (`LEGAL_PDF_MAX_BYTES`, PDF): большой или не тот файл не уходит в сеть.
 * Содержимое (битый, с паролем) проверяет только сервер — его ответ
 * показывается под блоком файла (`error`), а не исчезающим toast: человеку
 * нужно прочитать, что сделать с файлом.
 */

import { LEGAL_ERROR_MESSAGES, LEGAL_PDF_MAX_BYTES } from "@chaiberi/shared";
import { useRef, useState } from "react";

import type { LegalDocumentDto } from "../../lib/api-legal";
import { uploadLegalFile } from "../../lib/api-legal";
import { isUploadAborted, legalErrorText } from "../../lib/legal/legal-errors";

/** Похож ли файл на PDF по типу или расширению (точная проверка — на сервере по байтам). */
export function looksLikePdf(file: Pick<File, "name" | "type">): boolean {
  return file.type === "application/pdf" || /\.pdf$/iu.test(file.name);
}

/** Ошибка файла до отправки или null. */
export function legalFilePrecheck(file: Pick<File, "name" | "type" | "size">): string | null {
  if (!looksLikePdf(file)) return LEGAL_ERROR_MESSAGES.legal_file_not_pdf;
  if (file.size > LEGAL_PDF_MAX_BYTES) return LEGAL_ERROR_MESSAGES.legal_file_too_large;
  return null;
}

export interface LegalFileUpload {
  /** Идёт загрузка: имя и процент. */
  progress: { name: string; percent: number } | null;
  error: string | null;
  /** Загрузить в документ `docId`; результат — карточка из ответа или null. */
  start: (docId: string, file: File) => Promise<LegalDocumentDto | null>;
  cancel: () => void;
  clearError: () => void;
  /** Проверить файл до отправки: ошибка — под блоком, результат — годится ли файл. */
  check: (file: File) => boolean;
}

export function useLegalFileUpload(): LegalFileUpload {
  const [progress, setProgress] = useState<LegalFileUpload["progress"]>(null);
  const [error, setError] = useState<string | null>(null);
  const controller = useRef<AbortController | null>(null);

  async function start(docId: string, file: File): Promise<LegalDocumentDto | null> {
    const precheck = legalFilePrecheck(file);
    if (precheck) {
      setError(precheck);
      return null;
    }
    setError(null);
    controller.current?.abort();
    const abort = new AbortController();
    controller.current = abort;
    setProgress({ name: file.name, percent: 0 });
    try {
      return await uploadLegalFile(docId, file, {
        signal: abort.signal,
        onProgress: (percent) => setProgress({ name: file.name, percent }),
      });
    } catch (err) {
      if (!isUploadAborted(err)) setError(legalErrorText(err, "Не удалось загрузить файл"));
      return null;
    } finally {
      if (controller.current === abort) controller.current = null;
      setProgress(null);
    }
  }

  return {
    progress,
    error,
    start,
    cancel: () => controller.current?.abort(),
    clearError: () => setError(null),
    check: (file) => {
      const precheck = legalFilePrecheck(file);
      setError(precheck);
      return precheck === null;
    },
  };
}
